import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  DownloadOutlined,
  WarningOutlined,
  SaveOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Row, Table, Modal, Col, Spin, Input, Upload, Tooltip, Typography } from "antd";
import ContractualRevenue from "../../../../../services/api/ContractualRevenue";
import { setLoading } from "../../../../../redux/ducks/Loading";
import {
  exportFile,
  formatNumber,
  showError,
  showSuccess,
  trimData,
  unformatNumber,
} from "../../../../../utils/utilities";
import { useParams } from "react-router-dom";
import { get, indexOf, isEmpty, toLower } from "lodash";
import Button from "../../../../../components/functional_components/Button/PFButton";
import { useFormik } from "formik";
import {
  fileFormatXextension,
  langMonth,
  permittedFileFormatUpload,
  permittedFileFormatUploadName,
} from "../../../../../utils/properties";
import Project from "../../../../../services/api/Project";
import ContractualRevenueDocument from "../../../../../services/api/ContractualRevenueDocument";
import dayjs from "dayjs";
const { Dragger } = Upload;
const { Text } = Typography;
const { confirm } = Modal;

const ContractualRevenues = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const { t } = useTranslation();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const dateFormat = langMonth[toLower(currentLanguage)];
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const projectManagement = useSelector((state) => get(state.ProjectManagementDuck, "project", {}));
  const openPlanning = useSelector((state) => get(state.ProjectManagementDuck, "openPlanning", {}));
  const closed = useSelector((state) => get(state.ProjectManagementDuck, "closed", {}));
  const [state, setState] = useState({
    tableContent: [],
    modal: {
      visible: false,
      type: "",
      data: {},
    },
    loading: false,
    fileList: [],
    startFileList: [],
    dirtyFileList: false,
    totalAmount: 0,
  });

  useEffect(() => {
    getData(params.id);
  }, [projectManagement]);

  const getData = async (id) => {
    dispatch(setLoading(true));
    const contractualRevenues = await Project.getContractualRevenues(id);
    if (!contractualRevenues.error) {
      contractualRevenues.data.forEach((cc, i) => {
        cc.key = i; // This is necessary to remove warning in console for unique key row
      });

      setState((prevState) => ({
        ...prevState,
        tableContent: contractualRevenues.data,
        totalAmount: contractualRevenues.totalAmount,
      }));
    } else {
      showError(get(contractualRevenues, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const columns = [
    {
      title: t("projectsManagement.ProjectForm.contractualRevenues.month"),
      dataIndex: "revenueDate",
      key: "month",
      render: (date) => (date ? dayjs(date, "YYYY-MM").format(dateFormat) : ""),
    },
    {
      title: t("projectsManagement.ProjectForm.contractualRevenues.description"),
      dataIndex: "description",
      key: "description",
    },
    {
      title: t("projectsManagement.ProjectForm.contractualRevenues.value"),
      dataIndex: "amount",
      className: "table-value-right",
      key: "value",
      render: (value) => formatNumber({ value, suffix: "EUR", lang: currentLanguage, decimalScale: 0 }),
    },
    {
      title: t("projectsManagement.ProjectForm.contractualRevenues.salesforceID"),
      dataIndex: "salesforceId",
      key: "salesforceID",
    },
    {
      key: "action",
      render: (data) => {
        return (
          <div key={data.id} className="flex-end">
            {data.hasDocument === false ? (
              <div>
                <WarningOutlined className="PFred mr-10 icon-small noCursorPointer" />
              </div>
            ) : (
              <div className="mr-10" onClick={handleDownload(data)}>
                <DownloadOutlined className="icon-small" />
              </div>
            )}
            <div
              className="mr-10"
              onClick={userInfo.canEditProjectForm && projectManagement.enabled ? openDocumentModal(data) : undefined}
            >
              <EditOutlined
                className={`icon-small ${
                  userInfo.canEditProjectForm && projectManagement.enabled ? "" : "disabled noCursorPointer"
                }`}
              />
            </div>
            <div
              className="mr-10"
              onClick={
                userInfo.canEditProjectForm && data.editable && projectManagement.enabled
                  ? deleteCallback(data.id)
                  : undefined
              }
            >
              <DeleteOutlined
                className={`icon-small ${
                  userInfo.canEditProjectForm && data.editable && projectManagement.enabled
                    ? ""
                    : "disabled noCursorPointer"
                }`}
              />
            </div>
          </div>
        );
      },
    },
  ];

  const openDocumentModal =
    (item = {}) =>
    async () => {
      if (isEmpty(item)) {
        setState((prevState) => ({ ...prevState, modal: { visible: true, type: "new", data: {} } }));
      } else {
        let documentList = [];
        const listDocuments = await ContractualRevenue.getDocumentContractualRevenue(item.id);
        if (!listDocuments.error && listDocuments.data) {
          documentList = listDocuments.data.map((f) => ({
            uid: f.id,
            id: f.id,
            name: f.description,
            type: "." + f.fileExtension,
          }));
        } else {
          showError(get(listDocuments, "error.messageCode", "ER000"));
        }
        setState((prevState) => ({
          ...prevState,
          modal: { visible: true, type: "edit", data: item },
          fileList: documentList,
          startFileList: get(listDocuments, "data", []),
        }));
      }
    };

  const closeModal = () => {
    formikContractualRevenuesForm.resetForm();
    getData(params.id);
    setState((prevState) => ({
      ...prevState,
      modal: { visible: false, type: "", data: {} },
      fileList: [],
      startFileList: [],
      dirtyFileList: false,
    }));
  };

  const formikContractualRevenuesForm = useFormik({
    initialValues: {
      description: get(state.modal, "data.description", ""),
      value: get(state.modal, "data.amount", 0),
      salesforceID: get(state.modal, "data.salesforceId", ""),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!(values.description && /^[a-zA-Z0-9àéèìòù_-\s]{1,50}$/.test(values.description))) {
        errors.description = "Required!";
      }
      if (!values.value) {
        errors.value = "Required!";
      }
      if (isEmpty(state.fileList)) {
        // attached file is required only if project is in status "P5"
        if (projectManagement.projectEstimateStatusId === "P5") {
          errors.file = "Required!";
        }
      } else {
        for (const file of state.fileList) {
          if (
            indexOf(permittedFileFormatUpload, file.type) === -1 && // for files just uploaded by the user
            indexOf(permittedFileFormatUploadName, file.type) === -1 // for files already uploaded (in edit modal)
          ) {
            errors.file = "File format not allowed";
            break;
          }
          if (file.size === 0) {
            errors.file = "File empty";
            break;
          }
        }
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      let validEdit = true;
      let responseSave = [];
      setState((prevState) => ({ ...prevState, loading: true }));
      if (formikContractualRevenuesForm.dirty) {
        let data = {
          projectId: params.id,
          description: values.description,
          amount: unformatNumber(values.value, currentLanguage),
          salesforceId: values.salesforceID,
          id: get(state.modal, "data.id", undefined),
        };
        const response = await ContractualRevenue.saveNewContractualRevenue(data);
        if (!response.error && response.data) {
          responseSave = response.data;
        } else {
          validEdit = false;
          showError(get(response, "error.messageCode", "ER000"));
        }
      }
      if (validEdit && !isEmpty(state.fileList)) {
        const checkAlreadyExsist = Object.keys(state.startFileList).map((sf) => state.startFileList[sf].id);
        for (const file of state.fileList) {
          if (indexOf(checkAlreadyExsist, file.id) === -1) {
            const formData = new FormData();
            formData.append("file", file);
            const document = await ContractualRevenue.saveDocumentContractualRevenue(
              state.modal.type === "new" ? responseSave.id : state.modal.data.id,
              formData
            );
            if (!document.error) {
              showSuccess("SuccessDucumentNotificationContent", file.name);
            } else {
              showError("ErrorDucumentNotificationContent", file.name);
            }
          }
        }
        getData(params.id);
      }
      closeModal();
      setState((prevState) => ({ ...prevState, loading: false, fileList: [] }));
    },
  });

  const deleteCallback = (id) => async () => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        dispatch(setLoading(true));
        const response = await ContractualRevenue.deleteContractualRevenue(id);
        if (!response.error) {
          showSuccess();
          getData(params.id);
        } else {
          showError(get(response, "error.messageCode", "ER000"));
        }
        dispatch(setLoading(false));
      },
      onCancel() {},
    });
  };

  const handleDownload = (data) => async () => {
    dispatch(setLoading(true));
    const listDocuments = await ContractualRevenue.getDocumentContractualRevenue(data.id);
    if (!listDocuments.error && listDocuments.data) {
      for (const file of listDocuments.data) {
        const fileDownload = await ContractualRevenueDocument.downloadDocumentContractualRevenue(file.id);
        if (!fileDownload.error) {
          const downloadType = fileFormatXextension[file.fileExtension];
          const blob = new Blob([fileDownload], { type: downloadType });
          exportFile(blob, `${file.description}`);
          showSuccess("SuccessDownloadDocumentNotificationContent", file.description);
        } else {
          showError("ErrorDownloadDocumentNotificationContent", file.description);
        }
      }
    } else {
      showError(get(listDocuments, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const props = {
    multiple: true,
    onChange: () => {
      formikContractualRevenuesForm.validateForm();
    },
    onRemove: async (file) => {
      const index = state.fileList.indexOf(file);
      const newFileList = state.fileList.slice();
      newFileList.splice(index, 1);
      if (file.id) {
        dispatch(setLoading(true));
        const deleteFile = await ContractualRevenueDocument.deleteDocumentContractualRevenue(file.id);
        if (deleteFile.error) {
          showError("ErrorDucumentNotificationContent", file.name);
        }
        dispatch(setLoading(false));
      }
      setState((prevState) => ({
        ...prevState,
        fileList: newFileList,
        dirtyFileList: true,
      }));
      formikContractualRevenuesForm.validateForm();
    },
    beforeUpload: (file) => {
      const ckFileList = state.fileList;
      if (indexOf(permittedFileFormatUpload, file.type) === -1) {
        showError(t("projectEstimate.modal.errorFileFormat"));
      } else if (file.size === 0) {
        showError(t("projectEstimate.modal.errorFileEmpty"));
      } else {
        ckFileList.push(file);
      }
      setState((prevState) => ({
        ...prevState,
        fileList: ckFileList,
        dirtyFileList: true,
      }));
      return false;
    },
    fileList: state.fileList,
  };

  const checkDisabledButton = () => {
    let returnValue = false;
    if (userInfo.canEditProjectForm && projectManagement.enabled) {
      // If I can edit and the project is enabled, I check the month.
      if (!closed || !openPlanning) {
        // If the month has been closed but the variable "openPlanning" is true then the button is enabled. If the month is open or closed but the variable "openPlanning" is false then I check if the month is approved.
        returnValue = projectManagement.approved; // If the month has been approved the button is disabled else is enabled se il mese è stato approvato il bottone è disabilitato, altrimenti è abilitato.
      }
    } else {
      returnValue = true;
    }
    return returnValue;
  };

  return (
    <>
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          isDisabled={checkDisabledButton()}
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={openDocumentModal()}
        />
      </Row>
      <Table bordered dataSource={state.tableContent} columns={columns} size="small" pagination={false} />
      <Row type="flex" align="middle" justify="start" className="mt-1em mb-10">
        <Col span={4}>
          <Text>{t("projectsManagement.ProjectForm.contractualRevenues.total")}</Text>
        </Col>
        <Col span={6}>
          <div className="boxValue">
            {formatNumber({ value: state.totalAmount, suffix: "EUR", lang: currentLanguage, decimalScale: 0 })}
          </div>
        </Col>
      </Row>

      {state.modal.visible && (
        <Modal
          width={700}
          title={
            state.modal.type === "edit" ? (
              <b>{t("projectsManagement.ProjectForm.contractualRevenues.modal.edit")}</b>
            ) : (
              <b>{t("projectsManagement.ProjectForm.contractualRevenues.modal.new")}</b>
            )
          }
          open={state.modal.visible}
          onCancel={closeModal}
          footer={[
            <Button
              key="okButton"
              currentIcon={<SaveOutlined className="icon-medium" />}
              clickCallback={formikContractualRevenuesForm.submitForm}
              isDisabled={
                state.dirtyFileList
                  ? !formikContractualRevenuesForm.isValid
                  : !(formikContractualRevenuesForm.dirty && formikContractualRevenuesForm.isValid)
              }
            />,
          ]}
        >
          {state.loading && (
            <div className="container-spinner-positioning">
              <Spin spinning={state.loading} className="spinner-positioning" />
            </div>
          )}
          <Row gutter={[40, 48]}>
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <Col span={12}>
                  <label className="fw-bold " htmlFor="description">
                    {t("projectsManagement.ProjectForm.contractualRevenues.description")}*
                  </label>
                </Col>
                <Col span={12} className="right">
                  <Tooltip
                    title={
                      <div>
                        <p>{t("projectEstimate.modal.infoTooltip.maxLenght")}</p>
                        <p>{t("projectEstimate.modal.infoTooltip.specialChar")}</p>
                      </div>
                    }
                  >
                    <InfoCircleOutlined className="icon-small" />
                  </Tooltip>
                </Col>
              </Row>
              <Row>
                <Input
                  disabled={state.modal.type === "new" ? checkDisabledButton() : !state.modal.data.editable}
                  className={formikContractualRevenuesForm.errors.description !== undefined && "input-error"}
                  required
                  id="description"
                  name="description"
                  showCount
                  maxLength={50}
                  onChange={formikContractualRevenuesForm.handleChange}
                  onBlur={formikContractualRevenuesForm.handleBlur}
                  value={formikContractualRevenuesForm.values.description}
                />
              </Row>
            </Col>
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <label className="fw-bold" htmlFor="value">
                  {t("projectsManagement.ProjectForm.contractualRevenues.value")}*
                </label>
              </Row>
              <Row>
                {formatNumber({
                  name: `value`,
                  value: formikContractualRevenuesForm.values.value,
                  lang: currentLanguage,
                  id: `value`,
                  displayType: "input",
                  suffix: "EUR",
                  customInput: Input,
                  onChange: formikContractualRevenuesForm.handleChange,
                  onBlur: formikContractualRevenuesForm.handleBlur,
                  className: "right",
                  disabled: state.modal.type === "new" ? checkDisabledButton() : !state.modal.data.editable,
                })}
              </Row>
            </Col>
          </Row>
          <Row gutter={[40, 24]}>
            <Col span={12}>
              <Row>
                <label className="fw-bold mt-10" htmlFor="salesforceID">
                  {t("projectsManagement.ProjectForm.contractualRevenues.salesforceID")}
                </label>
              </Row>
              <Row>
                <Input
                  disabled={state.modal.type === "new" ? checkDisabledButton() : !state.modal.data.editable}
                  required
                  id="salesforceID"
                  name="salesforceID"
                  onChange={formikContractualRevenuesForm.handleChange}
                  onBlur={formikContractualRevenuesForm.handleBlur}
                  value={formikContractualRevenuesForm.values.salesforceID}
                />
              </Row>
            </Col>
            <Col span={24}>
              <Row>
                <Col span={24}>
                  <p>
                    {t("projectEstimate.modal.allowedExtensions")}{" "}
                    {permittedFileFormatUploadName.map((ext, i) =>
                      i < permittedFileFormatUploadName.length - 1 ? `${ext}, ` : ext
                    )}
                  </p>
                </Col>
                <Col span={24}>
                  <div>
                    <Dragger
                      {...props}
                      className={
                        formikContractualRevenuesForm.errors.file !== undefined &&
                        state.dirtyFileList &&
                        "borderRedDragger"
                      }
                      disabled={state.modal.type === "new" ? checkDisabledButton() : !state.modal.data.editable}
                    >
                      <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                      </p>
                      <p className="ant-upload-text">
                        {t("projectsManagement.ProjectForm.contractualRevenues.uploadTextMultiple")}*
                      </p>
                    </Dragger>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Modal>
      )}
    </>
  );
};

export default ContractualRevenues;
